/**
 * 验证码输入相关的自定义 Hook
 * 
 * 这个 Hook 专注于验证码输入的UI状态管理：
 * - 验证码输入状态管理
 * - 倒计时功能
 * - 重新发送状态管理
 * 
 * 不包含任何业务逻辑和API请求，纯UI状态管理
 */

import { useState, useEffect, useCallback } from 'react'

export interface UseVerificationCodeOptions {
  /** 验证码有效时长（秒），默认60秒 */
  initialCountdown?: number
  /** 验证码提交成功回调 */
  onSubmit?: (code: string) => void
  /** 是否在组件初始化时自动开始倒计时，默认false */
  autoStartCountdown?: boolean
}

export interface UseVerificationCodeReturn {
  /** 当前输入的验证码 */
  code: string
  /** 倒计时剩余秒数 */
  countdown: number
  /** 是否可以重新发送验证码 */
  canResend: boolean
  /** 验证码是否有效（6位数字） */
  isCodeValid: boolean
  /** 处理验证码输入变化 */
  handleCodeChange: (value: string) => void
  /** 处理验证码提交 */
  handleSubmit: () => void
  /** 重置验证码输入 */
  resetCode: () => void
  /** 开始倒计时 */
  startCountdown: (seconds?: number) => void
}

/**
 * 验证码输入 Hook
 * 
 * @param options - Hook 配置选项
 * @returns Hook 返回值
 */
export function useVerificationCode(options: UseVerificationCodeOptions = {}): UseVerificationCodeReturn {
  const { initialCountdown = 60, onSubmit, autoStartCountdown = false } = options

  // 验证码输入状态
  const [code, setCode] = useState('')

  // 倒计时状态
  const [countdown, setCountdown] = useState(autoStartCountdown ? initialCountdown : 0)

  // 倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  /**
   * 处理验证码输入变化
   * 只允许输入数字，最多6位
   */
  const handleCodeChange = useCallback((value: string) => {
    const numericValue = value.replace(/\D/g, '').slice(0, 6)
    setCode(numericValue)
  }, [])

  /**
   * 重置验证码输入
   */
  const resetCode = useCallback(() => {
    setCode('')
  }, [])

  /**
   * 处理验证码提交
   */
  const handleSubmit = useCallback(() => {
    if (code.length === 6 && onSubmit) {
      onSubmit(code)
      resetCode()
    }

  }, [code, onSubmit, resetCode])

  /**
   * 开始倒计时
   * @param seconds - 倒计时秒数，默认使用初始配置
   */
  const startCountdown = useCallback((seconds?: number) => {
    setCountdown(seconds || initialCountdown)
  }, [initialCountdown])

  // 计算派生状态
  const canResend = countdown === 0
  const isCodeValid = code.length === 6

  return {
    code,
    countdown,
    canResend,
    isCodeValid,
    handleCodeChange,
    handleSubmit,
    resetCode,
    startCountdown
  }
}